import { subMonths } from 'date-fns';
import { createAction } from 'redux-actions';

import { getCduUserLogon } from '@gef-ui/cdu-user/selectors';
import { resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';

import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import { formDataFieldsMap, quickFilterStates } from 'features/views/roboAdvisory/constants';
import userContext from 'features/views/userContext';
import { toIsoDateFormat, trimFormData } from 'utils';
import { getExactlyXYearsAgoAsDateObj } from 'utils/apiDateTime';

import * as types from './actionTypes';
import { FIELDS, ROBO_ADVISORY_FORM_NAME } from './form';
import { getAreAllShownRaRecordsChecked, getQuickFilterState, getShownRAAnalysisIds } from './selectors';

const url = fetch.constants.urls.raList;

export const reset = createAction(types.RESET);
export const changeQuickFilter = createAction(types.CHANGE_QUICK_FILTER, (type) => ({ type }));
export const setSort = createAction(types.SET_SORT, (sort) => ({ sort }));
export const changeRaAnalysisChecked = createAction(types.CHANGE_RA_ANALYSIS_CHECKED, (id) => ({ id }));
export const changeCheckAll = createAction(types.CHANGE_CHECK_ALL, (value) => ({ value }));
export const toggleCheckAll = createAction(types.TOGGLE_CHECK_ALL);
export const setRAAnalysis = createAction(types.SET_RA_ANALYSIS, (raAnalysisRecords) => ({ raAnalysisRecords }));
export const appendRAAnalysis = createAction(types.APPEND_RA_ANALYSIS, (raAnalysisRecords) => ({ raAnalysisRecords }));
export const changeAllShownRecordsToChecked = createAction(
	types.CHANGE_ALL_SHOWN_RECORDS_TO_CHECKED,
	(shownRaAnalysisIds) => ({ shownRaAnalysisIds }),
);

export const setInitialFormValues =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		const dateSixMonthsBack = subMonths(new Date(), 6);
		sendControlNext(
			ROBO_ADVISORY_FORM_NAME,
			setValue({ fieldName: FIELDS.createdDateFrom, value: dateSixMonthsBack }),
		);
		sendControlNext(ROBO_ADVISORY_FORM_NAME, setValue({ fieldName: FIELDS.createdDateTo, value: new Date() }));
	};

export const resetFormCriteria =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(reset());
		sendControlNext(ROBO_ADVISORY_FORM_NAME, resetForm());
		dispatch(fetch.actions.clearFetchStore(url));
		dispatch(setInitialFormValues());
	};

export const changeCheckBoxValue = (id) => (dispatch) => {
	dispatch(changeRaAnalysisChecked(id));
};

export const updateCheckAllCheckbox = () => (dispatch, getState) => {
	const areAllShownRaRecordsChecked = getAreAllShownRaRecordsChecked(getState());

	if (areAllShownRaRecordsChecked) {
		dispatch(changeCheckAll(true));
	} else {
		dispatch(changeCheckAll(false));
	}
};

export const changeAllRecordsCheckedValueWithCheckAllbox = () => (dispatch, getState) => {
	const shownRAAnalysisIds = getShownRAAnalysisIds(getState());
	dispatch(toggleCheckAll());
	dispatch(changeAllShownRecordsToChecked(shownRAAnalysisIds));
};

export const fetchRAAnalysis =
	(page = 0, sort = 'createdOn,desc') =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(ROBO_ADVISORY_FORM_NAME);
		const { personIn, createdDateFrom, createdDateTo, product, ...rest } = formData;

		const contextUser = userContext.selectors.getContextUser(getState());
		const mainUserLogin = getCduUserLogon(getState());
		const queryParams = { size: 20, page, sort };
		const body = {
			...rest,
			birthNo: personIn,
			createdDateFrom: toIsoDateFormat(createdDateFrom),
			createdDateTo: toIsoDateFormat(createdDateTo),
			productCode: product,
			userLogin: contextUser ? contextUser.login : mainUserLogin,
		};
		const quickFilterType = getQuickFilterState(getState());
		let finalFormData;

		dispatch(setSort(sort));
		dispatch(
			fetch.actions.post(url, {
				body,
				queryParams,
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				onSuccess: [
					(response) => () => {
						if (!response.body) return;
						const { data } = response.body;
						dispatch(setRAAnalysis(data));

						if (quickFilterType) {
							finalFormData = {};
							switch (quickFilterType) {
								case quickFilterStates.DONE:
									finalFormData.quickFilterDone = true;
									break;
								case quickFilterStates.DONE_WITH_POLICY:
									finalFormData.quickFilterDoneWithPolicy = true;
									break;
								case quickFilterStates.DRAFT:
									finalFormData.quickFilterDraft = true;
									break;
								case quickFilterStates.DONE_UNSIGNED:
									finalFormData.quickFilterDoneUnsigned = true;
									break;
							}
						} else {
							const cleansedFormData = trimFormData(formData);
							finalFormData = { ...cleansedFormData };
						}

						pushToFilterDataLayer({
							filterSection: 'Analýzy REDy',
							formData: finalFormData,
							formDataFieldsMap,
						});
					},
				],
			}),
		);
	};

export const loadWithQuickFilter =
	(type) =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(changeQuickFilter(type));

		let state;
		switch (type) {
			case quickFilterStates.DONE:
				state = [quickFilterStates.DONE, quickFilterStates.IN_PROGRESS];
				break;
			case quickFilterStates.DRAFT:
				state = [quickFilterStates.DRAFT];
				break;
			case quickFilterStates.DONE_WITH_POLICY:
				state = [quickFilterStates.DONE_WITH_POLICY];
				break;
			case quickFilterStates.DONE_UNSIGNED:
				state = [quickFilterStates.DONE_UNSIGNED];
				break;
		}

		sendControlNext(ROBO_ADVISORY_FORM_NAME, resetForm());
		const dateTwoYearsBack = getExactlyXYearsAgoAsDateObj(-2);
		sendControlNext(
			ROBO_ADVISORY_FORM_NAME,
			setValue({ fieldName: FIELDS.createdDateFrom, value: dateTwoYearsBack }),
		);
		sendControlNext(ROBO_ADVISORY_FORM_NAME, setValue({ fieldName: FIELDS.createdDateTo, value: new Date() }));
		sendControlNext(ROBO_ADVISORY_FORM_NAME, setValue({ fieldName: FIELDS.states, value: state }));

		dispatch(fetchRAAnalysis());
	};

export const loadSorted = (sort) => (dispatch, getState) => {
	const { queryParams, body } = fetch.selectors.getLastRequest(url)(getState());
	const updatedQueryParams = {
		...queryParams,
		sort,
	};

	dispatch(setSort(sort));
	dispatch(
		fetch.actions.post(url, {
			body,
			queryParams: updatedQueryParams,
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					const { data } = response.body;
					dispatch(setRAAnalysis(data));
				},
			],
		}),
	);
};

export const loadMore = () => (dispatch, getState) => {
	const { queryParams, body } = fetch.selectors.getLastRequest(url)(getState());
	const updatedQueryParams = {
		...queryParams,
		page: queryParams.page + 1,
	};

	dispatch(
		fetch.actions.post(url, {
			queryParams: updatedQueryParams,
			body,
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					const { data } = response.body;
					dispatch(appendRAAnalysis(data));
				},
			],
		}),
	);
};

export const loadPage = (page) => (dispatch, getState) => {
	const { queryParams, body } = fetch.selectors.getLastRequest(url)(getState());
	const updatedQueryParams = {
		...queryParams,
		page,
	};

	dispatch(
		fetch.actions.post(url, {
			queryParams: updatedQueryParams,
			body,
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					const { data } = response.body;
					dispatch(setRAAnalysis(data));
				},
			],
		}),
	);
};
